import React, { useState, useEffect } from 'react';

import type { Category } from '../../services/api';
import api from '../../services/api';

import ImageGallery from './ImageGallery';
import styles from './ShopGallery.module.css';

/**
 * ShopGallery Props
 */
type ShopGalleryProps = {
  readonly categoryId: string;
  readonly radius?: number;
  readonly autoRotate?: boolean;
  readonly autoRotateInterval?: number;
  readonly type?: 'portfolio' | 'shop';
};

/**
 * ShopGallery Component (Generic Filterable Gallery)
 *
 * Displays images with category filtering in a circular gallery layout.
 * This is a wrapper around ImageGallery that adds:
 * - Category filter tabs for switching between categories
 * - Automatic category fetching based on type (portfolio, shop, or courses)
 * - Optional price badges for shop items
 *
 * The component integrates with the portfolio's circular UI design,
 * showing images in a ring layout with space theme styling.
 *
 * Features:
 * - Category filter tabs ("All" + individual categories)
 * - Circular positioning using polar coordinates
 * - Manual navigation (next/previous)
 * - Optional auto-rotation
 * - Click to view fullscreen
 * - Smooth animations with Framer Motion
 * - Dynamic category fetching from API
 *
 * Usage:
 * <ShopGallery
 *   categoryId="all"
 *   radius={350}
 *   autoRotate={false}
 *   type="portfolio"
 * />
 * @param root0
 * @param root0.categoryId
 * @param root0.radius
 * @param root0.autoRotate
 * @param root0.autoRotateInterval
 * @param root0.type
 */
const ShopGallery: React.FC<ShopGalleryProps> = ({
  categoryId,
  radius = 350,
  autoRotate = false,
  autoRotateInterval = 3000,
  type = 'shop',
}) => {
  // State for category filtering
  const [categories, setCategories] = useState<ReadonlyArray<Category>>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [loading, setLoading] = useState(false);

  /**
   * Fetch categories based on type (portfolio, shop, or courses)
   */
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const response = await api.categories.getByType(type);
        setCategories(response.categories);
      } catch (error) {
        console.error(`Failed to fetch ${type} categories:`, error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [type]);

  /**
   * Handle category selection
   * @param categorySlug
   */
  const handleCategorySelect = (categorySlug: string) => {
    setSelectedCategory(categorySlug);
  };

  // Determine which category to display
  const displayCategoryId = selectedCategory === 'all' ? categoryId : selectedCategory;

  return (
    <div className={styles.shopGalleryContainer}>
      {/* Category Filter Tabs */}
      {categories.length > 1 && (
        <div className={styles.categoryTabs}>
          <span className={styles.categoriesLabel}>Categories:</span>
          <button
            className={selectedCategory === 'all' ? styles.activeTab : styles.tab}
            onClick={() => handleCategorySelect('all')}
            disabled={loading}
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat._id}
              className={selectedCategory === cat.slug ? styles.activeTab : styles.tab}
              onClick={() => handleCategorySelect(cat.slug)}
              disabled={loading}
            >
              {cat.name}
            </button>
          ))}
        </div>
      )}

      <ImageGallery
        key={`${type}-${displayCategoryId}`}
        categoryId={displayCategoryId}
        radius={radius}
        autoRotate={autoRotate}
        autoRotateInterval={autoRotateInterval}
        variant={type === 'shop' ? 'shop' : 'gallery'}
        type={type}
      />
    </div>
  );
};

export default ShopGallery;
